<template>
  <div
    class="card"
    v-show="currentCardId == 'long_reason'"
    v-loading.fullscreen.lock="fullscreenLoading"
    :element-loading-text="loadingText"
  >
    <div class="overflow-scroll-y">
      <div class="card-header">
        <h2>志望動機</h2>
        <p>志望動機を書くときはAI文章生成を活用しよう</p>
      </div>

      <div class="add-item button-block" v-if="globalVar.in_app_payments">
        <button
          @click="openReasonLongChat()"
          id="prime-reasonai-btn"
          type="success"
          class="primary-button"
        >
          <span>AIで文章作成</span>
        </button>
      </div>

      <YagiChatBot
        ref="chatBot"
        modal-name="shiboudoukisho-ai"
        :questions="aiQuestions"
        :value="formatData"
        :auto-skip-filled="true"
        :show-usage-count="true"
        :usage-count="aiUsageCount"
        :max-usage-count="aiMaxUsageCount"
        :usage-count-label="globalVar.isYagiPrime ? '1日利用回数' : '利用回数'"
        chat-type="shiboudoukisho"
        @input="onChatInput"
        @complete="generateAiMypr"
      >
        <template #result="{ result, close }">
          <div class="ai-result-panel">
            <div class="ai-result-panel__title">AIからの提案</div>
            <div class="ai-result-panel__body" v-html="result"></div>
            <div class="ai-result-panel__action">
              <el-button
                type="success"
                size="medium"
                @click="applyAiResult(String(result), close)"
                class="ai-result-panel__use"
              >
                この文章を使う
              </el-button>
            </div>
          </div>

          <div v-if="!globalVar.isYagiPrime" class="text-right ai-result-prime">
            全文を表示する場合は／<br />
            <el-button
              @click="goYagiprimeRegister"
              id="prime-reasonai-register-btn"
              v-if="globalVar.in_app_payments"
              class="ai-result-prime__btn"
            >
              ヤギプライム会員になる
            </el-button>
          </div>
        </template>
      </YagiChatBot>

      <div class="card-content">
        <el-input
          type="textarea"
          v-model="formatData.longreason"
          :autosize="{ minRows: 5, maxRows: 15 }"
        />
        <div class="attention-limit character" v-if="previewErrors.longreason">
          <i class="el-icon-warning"></i>
          入力可能な文字数をオーバーしています。文章を短くするか、入力欄の大きい別の履歴書を選んでください。
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount } from 'vue'
import YagiChatBot from '@/components/format-edit/yagi-chat-bot/YagiChatBot.vue'
import { useShibouDoukishoAi } from '@/composables/format-edit/useShibouDoukishoAi'
import { useShibouDoukishoFormatData } from '@/composables/format-edit/useShibouDoukishoFormatData'
import shibouDoukishoAiQuestions from '@/utils/format-edit/shibouDoukishoAiQuestions.json'
import type {
  YagiChatFormValue,
  YagiChatQuestion
} from '@/utils/format-edit/yagiChatBot'

defineProps<{
  currentCardId?: string
  previewErrors: Record<string, string>
}>()

const formatData = useShibouDoukishoFormatData()
const aiQuestions = shibouDoukishoAiQuestions as YagiChatQuestion[]

const {
  chatBot,
  fullscreenLoading,
  loadingText,
  aiUsageCount,
  aiMaxUsageCount,
  globalVar,
  generateAiMypr,
  applyAiResult,
  openReasonLongChat,
  goYagiprimeRegister,
  fetchAiUsageCount,
  autoFillFromRirekisho
} = useShibouDoukishoAi(formatData)

onBeforeMount(() => {
  autoFillFromRirekisho()
  fetchAiUsageCount()
})

function onChatInput(next: YagiChatFormValue) {
  Object.assign(formatData, next)
}
</script>

<style lang="scss" scoped>
.button-block {
  .primary-button {
    position: relative;
    background: #ff8c00;
    color: rgb(255 255 255);
    border: none;
    margin-right: 10px;
    padding: 12px 20px;
    border-radius: 4px;

    &::after {
      position: absolute;
      content: '✨';
      font-size: 20px;
      top: -5px;
      right: -7px;
    }
  }

  @media screen and (width <= 768px) {
    margin-right: 0;
    padding: 0;
    justify-content: center;
  }
}

.ai-result-panel {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  border: 1px solid #ddd;

  &__title {
    font-weight: bold;
    margin-bottom: 15px;
    color: #27ae60;
    font-size: 1.1rem;
  }

  &__body {
    line-height: 1.8;
    color: #333;
    margin-bottom: 20px;
  }

  &__action {
    text-align: center;
  }

  &__use {
    width: 100%;
    font-size: 1rem;
    padding: 12px 0;
  }
}

.ai-result-prime {
  margin-top: 10px;

  &__btn {
    color: #fff;
    background-color: #e3165c;
    border-color: #e3165c;
  }
}
</style>
